import { Injectable, signal } from '@angular/core';
import { GuidedTourAudience, GuidedTourContext, GuidedTourStep } from '../../models/guide/guided-tour.model';
import {
  buildPatientFullTour,
  buildProviderFullTour,
  getSearchTourSteps,
} from '../../utils/guided-tour-content.util';
import {
  markTourPromptAccepted,
  markTourPromptDismissed,
  shouldShowTourPrompt,
} from '../../utils/guided-tour-state.util';

@Injectable({ providedIn: 'root' })
export class GuidedTourService {
  readonly visible = signal(false);
  readonly audience = signal<GuidedTourAudience>('patient');
  readonly steps = signal<GuidedTourStep[]>([]);
  readonly promptVisible = signal(false);
  readonly promptAudience = signal<GuidedTourAudience | null>(null);

  private promptStart?: () => void;

  offerTour(audience: GuidedTourAudience, start: () => void): void {
    if (!shouldShowTourPrompt(audience)) return;
    if (this.visible() || this.promptVisible()) return;

    this.promptAudience.set(audience);
    this.promptStart = start;
    this.promptVisible.set(true);
  }

  acceptPrompt(): void {
    const audience = this.promptAudience();
    if (audience) {
      markTourPromptAccepted(audience);
    }

    const start = this.promptStart;
    this.closePrompt();
    start?.();
  }

  dismissPrompt(): void {
    const audience = this.promptAudience();
    if (audience) {
      markTourPromptDismissed(audience);
    }

    this.closePrompt();
  }

  suppressPrompt(audience: GuidedTourAudience): void {
    markTourPromptAccepted(audience);
  }

  startPatientTour(context: GuidedTourContext = {}): void {
    this.audience.set('patient');
    this.steps.set(buildPatientFullTour(context));
    this.visible.set(true);
  }

  startProviderTour(context: GuidedTourContext): void {
    this.audience.set('provider');
    this.steps.set(buildProviderFullTour(context));
    this.visible.set(true);
  }

  startSearchTour(): void {
    this.audience.set('search');
    this.steps.set(getSearchTourSteps());
    this.visible.set(true);
  }

  setVisible(visible: boolean): void {
    this.visible.set(visible);
  }

  private closePrompt(): void {
    this.promptVisible.set(false);
    this.promptAudience.set(null);
    this.promptStart = undefined;
  }
}
